import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useIntercom } from './hooks/useIntercom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Login from './components/Login';
import Events from './components/Events';
import EventGroups from './components/EventGroups';
import MultiRace from './components/MultiRace';
import Results from './components/Results';
import Upload from './components/Upload';
import SharedResults from './components/SharedResults';
import SharedGroupResults from './components/SharedGroupResults';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import Contact from './components/Contact';
import Profile from './components/Profile';
import Organization from './components/Organization';
import SuperAdminPanel from './components/SuperAdminPanel';
import Dashboard from './components/Dashboard';

function App() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-slate-100 flex items-center justify-center relative">
        {/* Subtle desaturated background layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100/20 via-slate-50/30 to-gray-200/20"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-100/10 via-transparent to-slate-100/10"></div>
        
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-300 border-t-blue-500"></div>
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-100/20 to-slate-200/20"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-slate-100 relative">
      {/* Desaturated multi-layer background with subtle depth */}
      <div className="fixed inset-0 bg-gradient-to-br from-gray-100/30 via-slate-50/40 to-gray-200/30"></div>
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-gray-100/20 via-transparent to-slate-100/20"></div>
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-slate-100/15 via-transparent to-gray-100/15"></div>
      
      <div className="relative z-10">
        <BrowserRouter>
          <IntercomWrapper />
          <Routes>
            {/* Public pages */}
            <Route path="/results/:eventId" element={<SharedResults />} />
            <Route path="/results/group/:groupId" element={<SharedGroupResults />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/contact" element={<Contact />} />
            
            {/* Auth routes */}
            <Route 
              path="/login" 
              element={
                user ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <Login />
                )
              } 
            />
            
            {/* Protected routes */}
            <Route element={<ProtectedLayout />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/events" element={<Events />} />
              <Route path="/multi-race" element={<MultiRace />} />
              <Route path="/upload/:eventId" element={<Upload />} />
              <Route path="/results" element={<Results />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/organization" element={<Organization />} />
              <Route path="/super-admin" element={<SuperAdminPanel />} />
            </Route>
            
            {/* Catch-all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </div>
  );
}

// Component to conditionally initialize Intercom
function IntercomWrapper() {
  const location = useLocation();
  
  // Check if current path is a public results page
  const isPublicResultsPage = 
    location.pathname.startsWith('/results/') && 
    (location.pathname.match(/^\/results\/[^/]+$/) || // /results/:eventId
     location.pathname.match(/^\/results\/group\/[^/]+$/)); // /results/group/:groupId
  
  // Only initialize Intercom if NOT on public results pages
  if (!isPublicResultsPage) {
    useIntercom();
  }
  
  return null;
}

// Protected layout wrapper component
function ProtectedLayout() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-slate-100 flex items-center justify-center relative">
        {/* Subtle desaturated background layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100/20 via-slate-50/30 to-gray-200/20"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-100/10 via-transparent to-slate-100/10"></div>
        
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-300 border-t-blue-500"></div>
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-100/20 to-slate-200/20"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="container mx-auto px-4 py-8 flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

export default App;